import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2, Sparkles, Upload, X, Check, Coins, RefreshCw, Wand2 } from "lucide-react";
import { GFFrame } from "@/components/GFFrame";
import {
  aiCreateCampaign, aiGetCampaign, aiGenerateStrategy, aiUploadImages,
  aiFinalizeCampaign, aiGetOwner, aiRecommend, aiUpdateCampaign, aiUploadLogo, aiRegenerateTagline,
  aiUseBrandKitLogo, aiBrandKitLogoUrl,
} from "@/lib/gfApi";
import { CreditChip, UpsellModal, GrowthContextCard, readAiEmail } from "@/pages/ai/aiShared";

const OBJECTIVES = [
  "Tingkatkan jualan produk dalam bulan ini",
  "Bina kesedaran jenama di media sosial",
  "Dapatkan lebih ramai leads / prospek",
  "Lancarkan produk atau promosi baharu",
  "Naikkan penglibatan pengikut sedia ada",
];

const inputCls = "w-full rounded-2xl border border-[#E7E2D6] bg-white px-4 py-3 text-[15px] outline-none focus:border-[#D4AF37]";

export default function CampaignWizard() {
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const resumeId = params.get("resume") || "";
  const [email, setEmail] = useState(readAiEmail() || params.get("email") || "");

  const [assessmentId, setAssessmentId] = useState(params.get("assessment_id") || "");
  const [owner, setOwner] = useState(null);
  const [recommend, setRecommend] = useState(null);
  const [ready, setReady] = useState(false);

  const [step, setStep] = useState(1);
  const [campaign, setCampaign] = useState(null);
  const [credits, setCredits] = useState(null);
  const [upsell, setUpsell] = useState(false);
  const [busy, setBusy] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const pollRef = useRef(null);

  const [objective, setObjective] = useState("");
  const [customMode, setCustomMode] = useState(false);
  const [product, setProduct] = useState({ name: "", description: "", price: "", usp: "", target_hint: "" });
  const [files, setFiles] = useState([]);
  const [logo, setLogo] = useState(null);
  const [useBrandLogo, setUseBrandLogo] = useState(false);
  const [tagline, setTagline] = useState("");
  const [tagBusy, setTagBusy] = useState(false);

  // Resolve identity + active Business Growth context + AI recommendation.
  useEffect(() => {
    (async () => {
      if (resumeId) {
        try {
          const c = await aiGetCampaign(resumeId);
          setCampaign(c);
          setAssessmentId(c.assessment_id);
          setObjective(c.objective || "");
          setProduct((p) => ({ ...p, ...(c.product_info || {}) }));
          const em = c.email || readAiEmail();
          if (em) { setEmail(em); aiGetOwner(em).then((ow) => { setOwner(ow); setCredits(ow.credits); }).catch(() => {}); }
          if (c.status === "completed") { navigate(`/campaigns/${c.id}`); return; }
          if (c.status === "generating" || c.status === "poster_failed") setStep(5);
          else setStep(c.strategy ? 3 : 2);
        } catch { toast.error("Kempen tidak dijumpai."); }
        setReady(true);
        return;
      }
      if (!email) { setReady(true); return; }
      try {
        const ow = await aiGetOwner(email);
        setOwner(ow);
        setCredits(ow.credits);
        const aid = params.get("assessment_id") || ow.active_assessment_id;
        setAssessmentId(aid || "");
        if (aid) {
          aiRecommend(aid).then((r) => {
            setRecommend(r);
            setObjective((o) => o || r.recommended_objective || OBJECTIVES[0]);
          }).catch(() => setObjective((o) => o || OBJECTIVES[0]));
        }
      } catch { /* ignore */ }
      setReady(true);
    })();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [resumeId, email]);

  // Load an initial tagline when the user reaches the final step (before generating).
  useEffect(() => {
    if (step !== 5 || !campaign?.id || tagline) return;
    if (campaign.poster_tagline) { setTagline(campaign.poster_tagline); return; }
    if (owner?.brand_kit?.tagline) { setTagline(owner.brand_kit.tagline); return; }
    regenTagline();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, campaign?.id]);

  if (!ready) {
    return (
      <GFFrame testId="wizard-page">
        <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#D4AF37]" /></div>
      </GFFrame>
    );
  }

  if (!email || (!assessmentId && !resumeId)) {
    return (
      <GFFrame testId="wizard-page">
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center">
          <p className="text-sm text-slate-500">Tiada laporan Business Growth aktif ditemui. Mulakan penilaian Business Growth dahulu.</p>
          <button onClick={() => navigate("/campaigns")} className="rounded-full bg-[#D4AF37] px-6 py-3 text-sm font-bold text-[#0F172A]">
            Ke Kempen Saya
          </button>
        </div>
      </GFFrame>
    );
  }

  const ctx = owner?.diagnosis_context || campaign?.diagnosis_context;

  const createAndStrategy = async () => {
    if (!product.name.trim()) { toast.error("Sila masukkan nama produk."); return; }
    setBusy(true);
    try {
      let c = campaign;
      if (!c) {
        c = await aiCreateCampaign({ email, assessment_id: assessmentId, objective, product_info: product });
      } else {
        c = await aiUpdateCampaign(c.id, { objective, product_info: product });
      }
      setCampaign(c);
      const withStrat = await aiGenerateStrategy(c.id);
      setCampaign(withStrat);
      setStep(3);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menjana strategi. Sila cuba lagi.");
    } finally {
      setBusy(false);
    }
  };

  const onPickFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    setFiles([...files, ...picked].slice(0, 5));
  };

  const uploadThenNext = async () => {
    setBusy(true);
    try {
      if (files.length > 0) {
        const fd = new FormData();
        files.forEach((f) => fd.append("files", f));
        await aiUploadImages(campaign.id, fd);
      }
      if (logo) {
        const lf = new FormData();
        lf.append("file", logo);
        await aiUploadLogo(campaign.id, lf);
      } else if (useBrandLogo) {
        await aiUseBrandKitLogo(campaign.id);
      }
      if (files.length > 0 || logo || useBrandLogo) setCampaign(await aiGetCampaign(campaign.id));
      const ow = await aiGetOwner(email).catch(() => null);
      if (ow) setCredits(ow.credits);
      setStep(5);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat naik gambar.");
    } finally {
      setBusy(false);
    }
  };

  const pollUntilDone = (cid) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const c = await aiGetCampaign(cid);
        setCampaign(c);
        if (c.status === "completed") {
          clearInterval(pollRef.current);
          toast.success("Kempen siap dijana!");
          navigate(`/campaigns/${c.id}`);
        } else if (c.status === "poster_failed") {
          clearInterval(pollRef.current);
          setFinalizing(false);
          toast.error(c.poster_error || "Poster gagal dijana. Cuba semula.");
        }
      } catch { /* keep polling */ }
    }, 3000);
  };

  const finalize = async () => {
    if ((credits?.usable ?? 0) < 1 && !campaign?.credit_deducted) { setUpsell(true); return; }
    setFinalizing(true);
    try {
      if (tagline.trim()) await aiUpdateCampaign(campaign.id, { tagline: tagline.trim() });
      const res = await aiFinalizeCampaign(campaign.id);
      setCampaign(res);
      if (res.status === "completed") { navigate(`/campaigns/${res.id}`); return; }
      pollUntilDone(campaign.id);
    } catch (e) {
      setFinalizing(false);
      if (e?.response?.status === 402) setUpsell(true);
      else toast.error(e?.response?.data?.detail || "Penjanaan gagal. Sila cuba lagi.");
    }
  };

  const regenTagline = async () => {
    if (!campaign?.id) return;
    setTagBusy(true);
    try {
      const r = await aiRegenerateTagline(campaign.id);
      setTagline(r.tagline || "");
    } catch {
      toast.error("Gagal menjana tagline. Cuba lagi.");
    } finally {
      setTagBusy(false);
    }
  };



  const s = campaign?.strategy || {};
  const posterFailed = campaign?.status === "poster_failed";

  return (
    <GFFrame testId="wizard-page">
      <div className="min-h-screen pb-16">
        <div className="bg-[#0F172A] px-6 pb-6 pt-9 text-white">
          <button onClick={() => (step > 1 && !resumeId ? setStep(step - 1) : navigate(-1))} data-testid="wizard-back-btn" className="flex items-center gap-1 text-sm text-slate-400">
            <ArrowLeft className="h-4 w-4" /> Kembali
          </button>
          <div className="mt-3 flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-[#D4AF37]" />
            <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#D4AF37]">Cipta Kempen · Langkah {step}/5</span>
          </div>
          <div className="mt-3 flex gap-1.5">
            {[1, 2, 3, 4, 5].map((n) => (
              <div key={n} className={`h-1.5 flex-1 rounded-full ${n <= step ? "bg-[#D4AF37]" : "bg-white/20"}`} />
            ))}
          </div>
          {credits ? <div className="mt-4"><CreditChip credits={credits} /></div> : null}
        </div>

        {/* Step 1: Growth context + AI recommendation */}
        {step === 1 && (
          <div className="px-6 pt-6" data-testid="wizard-step-1">
            {ctx && (
              <div className="mb-5">
                <GrowthContextCard ctx={ctx}
                  onReview={() => navigate(`/report/${assessmentId}`)} />
              </div>
            )}
            <p className="text-sm leading-relaxed text-slate-600">
              Berdasarkan diagnosis Business Growth anda, Brand2Market akan membantu anda membina kempen yang menyokong keutamaan ini.
            </p>

            {!customMode && (
              recommend ? (
                <div className="mt-4 rounded-2xl border-2 border-[#D4AF37] bg-[#FFFBEF] p-4" data-testid="ai-recommendation">
                  {recommend.marketing_not_priority && (
                    <div className="mb-3 rounded-xl bg-amber-100 p-3 text-xs text-amber-800" data-testid="rec-mnp">
                      Diagnosis anda menunjukkan bahawa pemasaran mungkin bukan perkara pertama yang perlu diperbaiki. {recommend.priority_note}
                      Anda masih boleh meneruskan kempen jika mahu.
                    </div>
                  )}
                  <p className="text-[11px] font-bold uppercase tracking-wide text-[#B8912B]">Daripada diagnosis anda, kami dapati:</p>
                  <p className="mt-1 text-sm text-slate-600">{ctx?.bottleneck_text || `Halangan utama: ${ctx?.bottleneck}`}</p>
                  <p className="mt-3 text-[11px] font-bold uppercase tracking-wide text-[#B8912B]">Kempen pertama yang kami cadangkan:</p>
                  <p className="mt-1 font-heading text-base font-bold text-[#0F172A]" data-testid="rec-objective">{recommend.recommended_objective}</p>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600"><b>Kenapa kempen ini?</b> {recommend.why}</p>
                  <button
                    data-testid="use-recommendation-btn"
                    onClick={() => { setObjective(recommend.recommended_objective); setStep(2); }}
                    className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-[#0F172A] py-3.5 text-sm font-bold text-white"
                  >
                    Guna Cadangan Ini <ArrowRight className="h-4 w-4" />
                  </button>
                  <button
                    data-testid="pick-other-objective-btn"
                    onClick={() => setCustomMode(true)}
                    className="mt-2 w-full rounded-full border border-[#E7E2D6] py-3 text-sm font-bold text-slate-600"
                  >
                    Saya Nak Pilih Objektif Lain
                  </button>
                </div>
              ) : (
                <div className="mt-6 flex items-center gap-2 text-sm text-slate-400" data-testid="rec-loading">
                  <Loader2 className="h-4 w-4 animate-spin text-[#D4AF37]" /> Menganalisis diagnosis anda…
                </div>
              )
            )}

            {customMode && (
              <div data-testid="objective-picker">
                <h2 className="mt-5 font-heading text-lg font-bold text-[#0F172A]">Pilih objektif kempen anda</h2>
                <div className="mt-3 space-y-2">
                  {OBJECTIVES.map((o, i) => (
                    <button
                      key={o} data-testid={`objective-${i}`}
                      onClick={() => setObjective(o)}
                      className={`w-full rounded-2xl border-2 p-4 text-left text-sm font-medium transition-colors ${objective === o ? "border-[#D4AF37] bg-[#FFFBEF] text-[#0F172A]" : "border-[#E7E2D6] text-slate-600"}`}
                    >
                      {o}
                    </button>
                  ))}
                </div>
                <button onClick={() => setStep(2)} data-testid="wizard-next-1" className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-[#0F172A] py-3.5 text-sm font-bold text-white">
                  Seterusnya <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Product info */}
        {step === 2 && (
          <div className="px-6 pt-6" data-testid="wizard-step-2">
            <h2 className="font-heading text-xl font-bold text-[#0F172A]">Maklumat produk / perkhidmatan</h2>
            <p className="mt-1 text-sm text-slate-500">AI akan gunakan ini + diagnosis anda untuk menjana strategi & copywriting.</p>
            <div className="mt-4 space-y-3">
              <input data-testid="product-name" className={inputCls} placeholder="Nama produk *" value={product.name} onChange={(e) => setProduct({ ...product, name: e.target.value })} />
              <textarea data-testid="product-description" rows={3} className={inputCls} placeholder="Penerangan ringkas" value={product.description} onChange={(e) => setProduct({ ...product, description: e.target.value })} />
              <input data-testid="product-price" className={inputCls} placeholder="Harga (cth: RM25)" value={product.price} onChange={(e) => setProduct({ ...product, price: e.target.value })} />
              <input data-testid="product-usp" className={inputCls} placeholder="Kelebihan unik (USP)" value={product.usp} onChange={(e) => setProduct({ ...product, usp: e.target.value })} />
              <input data-testid="product-target" className={inputCls} placeholder="Sasaran pelanggan (jika ada)" value={product.target_hint} onChange={(e) => setProduct({ ...product, target_hint: e.target.value })} />
            </div>
            <button onClick={createAndStrategy} disabled={busy} data-testid="wizard-generate-strategy" className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-[#D4AF37] py-3.5 text-sm font-bold text-[#0F172A] disabled:opacity-60">
              {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Menjana strategi AI…</> : <>Jana Strategi AI <Sparkles className="h-4 w-4" /></>}
            </button>
            {busy && <p className="mt-2 text-center text-xs text-slate-400">Ini mengambil masa ~20–40 saat. Sila tunggu.</p>}
          </div>
        )}

        {/* Step 3: Strategy review */}
        {step === 3 && (
          <div className="px-6 pt-6" data-testid="wizard-step-3">
            <h2 className="font-heading text-xl font-bold text-[#0F172A]">Strategi kempen anda</h2>
            {ctx && <p className="mt-1 text-xs text-slate-400">Menyokong halangan utama: <b className="text-[#B8912B]">{ctx.bottleneck}</b></p>}
            {s.marketing_not_priority && (
              <div className="mt-3 rounded-2xl border-l-4 border-amber-500 bg-amber-50 p-4" data-testid="wizard-mnp">
                <p className="text-xs font-bold uppercase tracking-wide text-amber-700">Nota Strategik</p>
                <p className="mt-1 text-sm text-slate-700">{s.marketing_not_priority_reason}</p>
              </div>
            )}
            <div className="mt-4 space-y-2 rounded-2xl border border-[#E7E2D6] bg-white p-4 text-sm text-slate-600">
              <p><b className="text-[#0F172A]">Objektif:</b> {s.campaign_objective}</p>
              <p><b className="text-[#0F172A]">Sasaran:</b> {s.primary_target}</p>
              <p><b className="text-[#0F172A]">Positioning:</b> {s.positioning}</p>
              <p><b className="text-[#0F172A]">Angle:</b> {s.campaign_angle}</p>
              <p><b className="text-[#0F172A]">Tawaran:</b> {s.offer}</p>
              <p><b className="text-[#0F172A]">Platform:</b> {s.recommended_platform}</p>
            </div>
            <div className="mt-3 rounded-2xl bg-[#0F172A] p-4 text-white">
              <p className="text-[11px] font-bold uppercase tracking-wide text-[#D4AF37]">Contoh Copy (Facebook)</p>
              <p className="mt-1.5 whitespace-pre-wrap text-sm text-slate-300">{s.facebook_copy}</p>
            </div>
            <p className="mt-3 text-center text-xs text-slate-400">Pek penuh (copy semua platform, pelan 7 hari, skrip WhatsApp) disertakan dalam playbook.</p>
            <button onClick={() => setStep(4)} data-testid="wizard-next-3" className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-[#0F172A] py-3.5 text-sm font-bold text-white">
              Teruskan ke Poster <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Step 4: Upload product photos + logo */}
        {step === 4 && (
          <div className="px-6 pt-6" data-testid="wizard-step-4">
            <h2 className="font-heading text-xl font-bold text-[#0F172A]">Gambar produk (1–5)</h2>
            <p className="mt-1 text-sm text-slate-500">AI akan menjana versi produk anda yang lebih menarik sebagai hero poster (pencahayaan & warna dipertingkatkan). Muat naik gambar produk sebenar sebagai rujukan. (Pilihan)</p>
            <label data-testid="upload-dropzone" className="mt-4 flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-[#D4AF37]/50 bg-[#FFFBEF] py-8 text-[#B8912B]">
              <Upload className="h-6 w-6" />
              <span className="text-sm font-semibold">Ketik untuk pilih gambar produk</span>
              <input type="file" accept="image/*" multiple className="hidden" onChange={onPickFiles} data-testid="upload-input" />
            </label>
            {files.length > 0 && (
              <div className="mt-4 grid grid-cols-3 gap-2" data-testid="upload-previews">
                {files.map((f, i) => (
                  <div key={i} className="relative">
                    <img src={URL.createObjectURL(f)} alt="" className="h-24 w-full rounded-xl object-cover" />
                    <button onClick={() => setFiles(files.filter((_, j) => j !== i))} className="absolute -right-1.5 -top-1.5 rounded-full bg-rose-500 p-1 text-white">
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <h3 className="mt-7 font-heading text-lg font-bold text-[#0F172A]">Logo jenama (pilihan)</h3>
            <p className="mt-1 text-sm text-slate-500">Logo anda akan diletakkan bersih & tajam di sudut poster — fail asal digunakan (tidak dijana semula). PNG latar telus paling elok.</p>
            {logo ? (
              <div className="mt-4 flex items-center gap-3 rounded-2xl border border-[#E7E2D6] bg-white p-3" data-testid="logo-preview">
                <img src={URL.createObjectURL(logo)} alt="logo" className="h-16 w-16 rounded-xl object-contain" />
                <span className="flex-1 truncate text-sm text-slate-600">{logo.name}</span>
                <button onClick={() => setLogo(null)} data-testid="logo-remove" className="rounded-full bg-rose-500 p-1.5 text-white">
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : useBrandLogo ? (
              <div className="mt-4 flex items-center gap-3 rounded-2xl border border-[#D4AF37] bg-[#FFFBEF] p-3" data-testid="brandkit-logo-selected">
                <img src={aiBrandKitLogoUrl(email)} alt="logo" className="h-16 w-16 rounded-xl object-contain" />
                <span className="flex-1 truncate text-sm text-[#B8912B]">Logo Brand Kit dipilih</span>
                <button onClick={() => setUseBrandLogo(false)} data-testid="brandkit-logo-unselect" className="rounded-full bg-rose-500 p-1.5 text-white">
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <>
                {owner?.brand_kit?.has_logo && (
                  <button onClick={() => setUseBrandLogo(true)} data-testid="use-brandkit-logo-btn"
                    className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl border border-[#D4AF37] bg-[#FFFBEF] py-3 text-sm font-bold text-[#B8912B]">
                    <Sparkles className="h-4 w-4" /> Guna logo Brand Kit saya
                  </button>
                )}
                <label data-testid="logo-dropzone" className="mt-3 flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-[#0F172A]/25 bg-white py-6 text-slate-500">
                  <Upload className="h-5 w-5" />
                  <span className="text-sm font-semibold">Ketik untuk pilih logo</span>
                  <input type="file" accept="image/png,image/jpeg,image/jpg" className="hidden" onChange={(e) => setLogo((e.target.files || [])[0] || null)} data-testid="logo-input" />
                </label>
              </>
            )}

            <button onClick={uploadThenNext} disabled={busy} data-testid="wizard-next-4" className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-[#0F172A] py-3.5 text-sm font-bold text-white disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{(files.length || logo || useBrandLogo) ? "Muat Naik & " : "Langkau & "}Teruskan <ArrowRight className="h-4 w-4" /></>}
            </button>
          </div>
        )}

        {/* Step 5: Finalize confirm */}
        {step === 5 && (
          <div className="px-6 pt-6" data-testid="wizard-step-5">
            <h2 className="font-heading text-xl font-bold text-[#0F172A]">Sahkan & jana kempen</h2>

            {finalizing || campaign?.status === "generating" ? (
              <div className="mt-6 flex flex-col items-center rounded-2xl border border-[#E7E2D6] bg-white p-8 text-center" data-testid="finalizing-state">
                <Loader2 className="h-8 w-8 animate-spin text-[#D4AF37]" />
                <p className="mt-4 text-sm font-semibold text-[#0F172A]">Menjana 3 poster & playbook…</p>
                <p className="mt-1 text-xs text-slate-400">Ini mengambil masa ~30–60 saat. Anda boleh tunggu di sini.</p>
              </div>
            ) : (
              <>
                {posterFailed && (
                  <div className="mt-4 rounded-2xl border-l-4 border-rose-500 bg-rose-50 p-4" data-testid="poster-failed-note">
                    <p className="text-sm font-semibold text-rose-700">{campaign.poster_error || "Poster anda belum berjaya dijana. Kredit anda belum ditolak. Cuba semula."}</p>
                  </div>
                )}

                <div className="mt-4 rounded-2xl border border-[#E7E2D6] bg-white p-5" data-testid="tagline-editor">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-bold text-[#0F172A]">Tagline poster</p>
                    <button onClick={regenTagline} disabled={tagBusy} data-testid="tagline-regen-btn"
                      className="flex items-center gap-1.5 rounded-full border border-[#D4AF37] px-3 py-1.5 text-xs font-bold text-[#B8912B] disabled:opacity-60">
                      {tagBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />} Jana Semula
                    </button>
                  </div>
                  <input
                    data-testid="tagline-input"
                    value={tagline}
                    onChange={(e) => setTagline(e.target.value)}
                    placeholder={tagBusy ? "Menjana tagline…" : "Hook pendek untuk poster"}
                    className={`mt-3 ${inputCls}`}
                  />
                  <p className="mt-1.5 text-[11px] text-slate-400">Maksimum 8 patah perkataan. Anda boleh edit atau tekan "Jana Semula".</p>
                </div>

                <div className="mt-4 rounded-2xl border border-[#E7E2D6] bg-white p-5">
                  <p className="text-sm text-slate-600">Kami akan menjana <b>3 poster berbeza</b> (format feed 1080×1350 + story 1080×1920) untuk anda pilih, serta <b>Campaign Playbook (PDF)</b> penuh. Playbook akan dihantar ke emel anda.</p>
                  <div className="mt-4 flex items-center justify-between rounded-xl bg-[#FFFBEF] px-4 py-3">
                    <span className="flex items-center gap-2 text-sm font-semibold text-[#0F172A]"><Coins className="h-4 w-4 text-[#D4AF37]" /> Kos</span>
                    <span className="font-heading text-lg font-bold text-[#B8912B]">1 Kredit</span>
                  </div>
                  <p className="mt-2 text-xs text-slate-400">Baki: {credits?.usable ?? 0} kredit. 1 kredit untuk semua 3 poster — ditolak hanya selepas poster berjaya dijana.</p>
                </div>

                {(credits?.usable ?? 0) < 1 && !campaign?.credit_deducted ? (
                  <div className="mt-6" data-testid="wizard-zero-credit">
                    <p className="text-center text-sm font-semibold text-[#0F172A]">Starter campaign credits anda telah digunakan.</p>
                    <button onClick={() => setUpsell(true)} data-testid="wizard-buy-credits-btn" className="mt-3 w-full rounded-full bg-[#D4AF37] py-3.5 text-sm font-bold text-[#0F172A]">
                      Beli Growth+ (RM39) atau Top-up (RM5)
                    </button>
                  </div>
                ) : (
                  <button onClick={finalize} data-testid="wizard-finalize-btn" className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-[#D4AF37] py-4 text-base font-bold text-[#0F172A]">
                    {posterFailed ? <><RefreshCw className="h-5 w-5" /> Cuba Semula Jana Poster</> : <><Check className="h-5 w-5" /> Jana Kempen Penuh</>}
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>
      <UpsellModal open={upsell} onClose={() => { setUpsell(false); aiGetOwner(email).then((o) => setCredits(o.credits)).catch(() => {}); }}
        email={email} assessmentId={assessmentId} zeroState={(credits?.usable ?? 0) < 1} />
    </GFFrame>
  );
}
